export const getEndUserTokenKeyByHandler = (handler) => `${handler}_endUserToken`;

const hasValue = (value) => {
    if (value === null || value === undefined) return false;
    if (typeof value === 'string') return value.trim().length > 0;
    return true;
};

/** Own-property lookup: a config key such as `toString` must miss these maps, not inherit from Object.prototype. */
const ownValue = (map, key) => (Object.prototype.hasOwnProperty.call(map, key) ? map[key] : undefined);

export const SELF_FIELD_MAP = {
    fname:        (eu) => hasValue(eu?.fname),
    lname:        (eu) => hasValue(eu?.lname),
    phone:        (eu) => hasValue(eu?.phone),
    imageUrl:     (eu) => hasValue(eu?.imageUrl),
    birth_date:   (eu) => hasValue(eu?.birth_date),
    idOrPassport: (eu) => hasValue(eu?.idOrPassport),
    social_link:  (eu) => hasValue(eu?.social_link),
};

/**
 * Required-field key -> the label the app already shows for that field on its own profile forms
 * (`family.*`). Deliberately NOT a new set of scanner-only strings: the guard reads out the field
 * name, and the family then opens CompleteFamilyProfileDialog and looks for that exact same words
 * -- "ת״ז / דרכון" at the gate, "ת״ז / דרכון" on the form. A parallel wording would be two names
 * for one field.
 */
export const PROFILE_FIELD_LABEL_KEYS = {
    fname: 'family.first_name',
    lname: 'family.last_name',
    imageUrl: 'family.photo',
    birth_date: 'family.date_of_birth',
    phone: 'family.phone',
    idOrPassport: 'family.id_or_passport',
    social_link: 'family.social_link',
};

/**
 * Turn the backend's `missingFields` keys into readable labels.
 *
 * The list is OPEN: required fields are per-seller config, so a key can arrive that this app has
 * no label for (`dynamicFields.membershipNo`, or a field added to a template after this build).
 * An unknown key is rendered rather than dropped -- a guard who reads out "membershipNo" can still
 * get the family to the right box, whereas a silently shortened list sends them away thinking they
 * are done. Unknown keys are wrapped in Unicode isolates so a Latin key inside a Hebrew sentence
 * cannot drag the surrounding punctuation to the wrong end of the line.
 */
export function missingFieldLabels(missingFields, translate) {
    if (!Array.isArray(missingFields)) return [];
    return missingFields
        .filter((field) => typeof field === 'string' && field.trim())
        .map((field) => {
            const labelKey = ownValue(PROFILE_FIELD_LABEL_KEYS, field);
            if (labelKey) return translate(labelKey);
            const raw = field.startsWith('dynamicFields.') ? field.slice('dynamicFields.'.length) : field;
            return `\u2068${raw}\u2069`;
        });
}

export function isSelfComplete(endUser, requiredFields) {
    if (!Array.isArray(requiredFields) || requiredFields.length === 0) return true;
    return requiredFields.every((field) => {
        if (typeof field !== 'string') return false;
        if (field.startsWith('dynamicFields.')) {
            const key = field.slice('dynamicFields.'.length);
            // Own-property here too: dynamicFields is buyer-shaped data, so a required
            // `dynamicFields.toString` would otherwise be satisfied by Object.prototype.
            return hasValue(ownValue(endUser?.dynamicFields ?? {}, key));
        }
        return ownValue(SELF_FIELD_MAP, field)?.(endUser) ?? false;
    });
}

/**
 * An explicit non-active status is a DIFFERENT refusal from an unfinished profile, and it
 * outranks it: a cancelled membership does not get in however complete the profile is. A missing
 * status reads as active -- only the backend's own word for it counts, so a response that omits
 * the field cannot label a live member "inactive".
 */
export const isMemberInactive = (member) => !!member?.status && member.status !== 'active';

/**
 * Why this member cannot be checked in, as one line for the gate.
 *
 * Status first. A member who is both cancelled and unfinished used to be told only what was
 * missing, so the family went off to add a photo and came back to the same refusal -- the real
 * blocker was never on screen.
 */
export function memberBlockReason(member, translate) {
    if (isMemberInactive(member)) return translate('scanner.member_inactive');
    if (!member?.isProfileComplete) {
        const fields = missingFieldLabels(member?.missingFields, translate);
        return fields.length > 0
            ? translate('scanner.member_missing', { fields: fields.join(', ') })
            : translate('scanner.member_incomplete');
    }
    return translate('scanner.member_inactive');
}
